/**
 * "Saiu versão nova" (pedido do dono, 15/10): when the desktop app opens, it asks the API for the
 * product's current version (GET /api/v1/app/version) and, when a newer one exists, says so once —
 * a quiet bar with the download link and an OS notification. "Depois" remembers that version, so
 * the bar returns only when a newer one ships. Web stays silent: the browser is always current.
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpCircle, X } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { APP_VERSION, isNewerVersion } from '../lib/version'
import { isDesktop, openExternal } from '../lib/desktop'
import { notify } from '../lib/inboxNotify'

const DISMISSED_KEY = 'devpulse:update-dismissed'

function dismissed(): string | null {
  try {
    return localStorage.getItem(DISMISSED_KEY)
  } catch {
    return null
  }
}

export function UpdateNotice() {
  const { t } = useI18n()
  const desktop = isDesktop()
  const [hidden, setHidden] = useState(false)
  const told = useRef(false)
  const { data } = useQuery({
    queryKey: ['app-version'],
    queryFn: api.app.version,
    enabled: desktop,
    staleTime: 12 * 60 * 60_000,
    retry: false,
  })

  const fresher = Boolean(desktop && data && isNewerVersion(APP_VERSION, data.version))
  const show = fresher && !hidden && dismissed() !== data!.version

  // The OS notification, once per discovery — the person may have the app in the background.
  useEffect(() => {
    if (show && !told.current) {
      told.current = true
      notify(t('update.title', { version: data!.version }), t('update.body'))
    }
  }, [show, data, t])

  if (!show) return null
  return (
    <div className="notice small row row--between row--wrap update-notice">
      <span className="row row--tight"><ArrowUpCircle size={15} aria-hidden /> {t('update.title', { version: data!.version })} {t('update.yours', { version: APP_VERSION })}</span>
      <span className="row row--tight">
        <button type="button" className="btn btn--sm btn--primary" onClick={() => void openExternal(data!.downloads)}>
          {t('update.get')}
        </button>
        <button
          type="button"
          className="btn btn--sm btn--ghost btn--icon"
          aria-label={t('update.later')}
          title={t('update.later')}
          onClick={() => {
            try {
              localStorage.setItem(DISMISSED_KEY, data!.version)
            } catch {
              // Private storage: hidden until the window closes.
            }
            setHidden(true)
          }}
        >
          <X size={14} />
        </button>
      </span>
    </div>
  )
}
